import { Inject, Injectable, Optional } from '@angular/core';

import { Observable, map } from 'rxjs';

import { IBetslipState } from '../../../../../base/store/state';
import { isLinear } from '../../../../../base/utils/linear-utils';
import { BetslipType } from '../../../../../core/betslip-type';
import { IBetslip, ISlip } from '../../../../bet-generation/models';
import { isBetBuilderSlipId } from '../../../../bet-generation/services/slip.utils';
import BetslipSummaryBuilder from '../../../../summary/services/betslip-summary-builder.service';
import { BetslipError } from '../../../errors/betslip-error';
import { BET_BUILDER_SLIP_VALIDATORS, CONTEXT_SLIP_VALIDATORS, EDIT_BET_SLIP_VALIDATORS } from '../../betslip-validation-injection-tokens';
import { SlipContextGeneratorService } from '../../generators/context/slip-context-generator.service';
import { TypeContextGeneratorService } from '../../generators/context/type-context-generator.service';
import { IBetBuilderSlipValidator, IContextSlipValidator, IEditBetSlipValidator } from '../../validators/slip/validators';
import BetValidationService from '../bets/bet-validation.service';

@Injectable()
export class SlipsValidationComposerService {
    constructor(
        @Optional() @Inject(EDIT_BET_SLIP_VALIDATORS) private editBetSlipValidators: IEditBetSlipValidator<BetslipError>[],
        @Optional() @Inject(CONTEXT_SLIP_VALIDATORS) private contextSlipValidators: IContextSlipValidator<BetslipError>[],
        @Optional() @Inject(BET_BUILDER_SLIP_VALIDATORS) private betBuilderSlipValidators: IBetBuilderSlipValidator<BetslipError>[],
        private typeContextGenerator: TypeContextGeneratorService,
        private slipContextGenerator: SlipContextGeneratorService,
        private betValidationService: BetValidationService,
        private betslipSummaryBuilder: BetslipSummaryBuilder,
    ) {}

    validateSlip$(slip: ISlip, betslip: IBetslip, state: IBetslipState): Observable<BetslipError[]> {
        return this.betslipSummaryBuilder.createBetslipSummary({ slips: [slip] }).pipe(
            map((summary) => {
                const possibleWinningsNet = summary.possibleWinningsNet;

                let errors: BetslipError[] = this.betValidationService.validateSlipBets(slip, possibleWinningsNet);

                if (this.contextSlipValidators?.length) {
                    const context = this.slipContextGenerator.generateContext(slip, betslip, state);

                    errors = [...errors, ...this.contextSlipValidators.flatMap((validator) => Array.from(validator.validate(slip, context)))];
                }

                if (state.types.base.currentSelectedType === BetslipType.EditBet && !!this.editBetSlipValidators?.length) {
                    const editBetContext = this.typeContextGenerator.generateEditBetContext(state.types.editBet);

                    errors = [...errors, ...this.editBetSlipValidators.flatMap((validator) => Array.from(validator.validate(slip, editBetContext)))];
                }

                if (isLinear(state) && isBetBuilderSlipId(slip.slipId)) {
                    const betBuilderContext = this.typeContextGenerator.generateBetBuilderContext(slip);

                    errors = [
                        ...errors,
                        ...this.betBuilderSlipValidators.flatMap((validator) => Array.from(validator.validate(slip, betBuilderContext))),
                    ];
                }

                return errors;
            }),
        );
    }
}
